import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DuplicateCheckStatus {
  checking: boolean;
  isDuplicate: boolean;
  existingContact: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

export function usePhoneDuplicateCheck(
  phone: string | null | undefined,
  countryCode: string | null | undefined = null,
  excludeContactId: string | null = null
): DuplicateCheckStatus {
  const [status, setStatus] = useState<DuplicateCheckStatus>({
    checking: false,
    isDuplicate: false,
    existingContact: null,
  });

  useEffect(() => {
    // Reset if phone is empty or too short
    if (!phone || phone.trim().length < 8) {
      setStatus({ checking: false, isDuplicate: false, existingContact: null });
      return;
    }

    const checkPhone = async () => {
      setStatus(prev => ({ ...prev, checking: true }));

      try {
        const { data, error } = await supabase
          .rpc('check_duplicate_contact_phone', {
            p_phone: phone.trim(),
            p_country_code: countryCode?.trim() || null,
            p_exclude_id: excludeContactId,
          })
          .maybeSingle();

        if (error) {
          console.error('Error checking phone duplicate:', error);
          setStatus({ checking: false, isDuplicate: false, existingContact: null });
          return;
        }

        setStatus({
          checking: false,
          isDuplicate: !!data,
          existingContact: data || null,
        });
      } catch (err) {
        console.error('Error checking phone duplicate:', err);
        setStatus({ checking: false, isDuplicate: false, existingContact: null });
      }
    };

    // Debounce: wait 500ms after user stops typing
    const timer = setTimeout(checkPhone, 500);
    return () => clearTimeout(timer);
  }, [phone, countryCode, excludeContactId]);

  return status;
}
